import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  Copy,
  Eye,
  FileText,
  ListChecks,
  Loader2,
  Pencil,
  Plus,
  Search,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { Main } from "@/components/Main";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AppDialogBody,
  AppDialogContent,
  AppDialogFooter,
  appDialogPrimaryButton,
  appDialogSecondaryButton,
} from "@/components/ui/app-dialog";
import {
  deleteSelection,
  downloadSelectionPdf,
  duplicateSelection,
  getSelectionLotteries,
  getSelections,
  PdfMode,
  SelectionListItem,
  SelectionListPage,
  SelectionLottery,
} from "@/Api/gameBankSelections";
import { getSelectContests } from "@/Api/contests";
import { usePermissions } from "@/hooks/usePermissions";
import { PERMISSIONS } from "@/constants/permissions";
import { LotteryBadge } from "../GameBank/shared";
import { PdfMenu } from "./PdfMenu";

const PER_PAGE = 10;

/** Abre o PDF numa aba nova (o navegador mostra o visualizador e dá para baixar de lá) */
export const openPdfBlob = (blob: Blob) => {
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener");
  // libera a memória depois que a aba carregou
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

/**
 * Banco de Bolões > Formador de Bolões > Bolões escolhidos (LOT-62).
 * Seleções salvas por concurso: acessar/editar, PDF, duplicar e excluir.
 */
export const GameSelectionsPage = () => {
  const navigate = useNavigate();
  const { hasPermission, isAdmin } = usePermissions();
  const canManage = isAdmin() || hasPermission(PERMISSIONS.MANAGE_GAME_SELECTIONS);

  const [lotteries, setLotteries] = useState<SelectionLottery[]>([]);
  const [data, setData] = useState<SelectionListPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [lotteryFilter, setLotteryFilter] = useState("all");
  const [applied, setApplied] = useState({ search: "", lottery: "all" });

  const [pdfLoading, setPdfLoading] = useState<string | null>(null);

  const [duplicateItem, setDuplicateItem] = useState<SelectionListItem | null>(null);
  const [contests, setContests] = useState<Array<{ id: number; name: string }>>([]);
  const [duplicateContestId, setDuplicateContestId] = useState("");
  const [duplicating, setDuplicating] = useState(false);

  const [deleteItem, setDeleteItem] = useState<SelectionListItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    getSelectionLotteries().then(({ ok, data: body }) => {
      if (ok && Array.isArray(body?.response)) setLotteries(body.response);
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { ok, data: body } = await getSelections({
        page,
        per_page: PER_PAGE,
        search: applied.search || undefined,
        lottery_id: applied.lottery !== "all" ? Number(applied.lottery) : undefined,
      });
      if (ok && body?.response) setData(body.response);
    } finally {
      setLoading(false);
    }
  }, [page, applied]);

  useEffect(() => {
    load();
  }, [load]);

  const applyFilters = () => {
    setPage(1);
    setApplied({ search, lottery: lotteryFilter });
  };

  const clearFilters = () => {
    setSearch("");
    setLotteryFilter("all");
    setPage(1);
    setApplied({ search: "", lottery: "all" });
  };

  const handlePdf = async (item: SelectionListItem, mode: PdfMode) => {
    setPdfLoading(item.uuid);
    try {
      const blob = await downloadSelectionPdf(item.uuid, mode);
      if (blob) openPdfBlob(blob);
    } finally {
      setPdfLoading(null);
    }
  };

  const openDuplicate = async (item: SelectionListItem) => {
    setDuplicateItem(item);
    setDuplicateContestId("");
    setContests([]);

    if (item.lottery) {
      const { ok, data: body } = await getSelectContests(item.lottery.id);
      if (ok && Array.isArray(body?.response)) {
        setContests(body.response.map((contest: { id: number; name: string }) => ({ id: contest.id, name: contest.name })));
      }
    }
  };

  const confirmDuplicate = async () => {
    if (!duplicateItem || !duplicateContestId) return;

    setDuplicating(true);
    try {
      const { ok, data: body } = await duplicateSelection(duplicateItem.uuid, Number(duplicateContestId));

      if (ok && body?.response?.uuid) {
        toast.success("Seleção duplicada. Escolha os jogos do novo concurso.");
        setDuplicateItem(null);
        navigate(`/formador-de-boloes/${body.response.uuid}`);
      }
    } finally {
      setDuplicating(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;

    setDeleting(true);
    try {
      const { ok } = await deleteSelection(deleteItem.uuid);

      if (ok) {
        toast.success("Seleção excluída. Os jogos voltaram ao banco.");
        setDeleteItem(null);
        await load();
      }
    } finally {
      setDeleting(false);
    }
  };

  const rowActions = (item: SelectionListItem) => (
    <div className="flex flex-wrap items-center gap-2 lg:flex-nowrap">
      <Button
        size="sm"
        className={appDialogSecondaryButton}
        onClick={() => navigate(`/formador-de-boloes/${item.uuid}/visualizar`)}
      >
        <Eye className="mr-1.5 h-4 w-4" />
        Visualizar
      </Button>
      {canManage && (
        <Button
          size="sm"
          className="bg-blue-600 text-white hover:bg-blue-700"
          onClick={() => navigate(`/formador-de-boloes/${item.uuid}`)}
        >
          <Pencil className="mr-1.5 h-4 w-4" />
          Editar
        </Button>
      )}
      <PdfMenu
        size="sm"
        className={appDialogSecondaryButton}
        label="PDF"
        icon={FileText}
        loading={pdfLoading === item.uuid}
        boloesCount={item.boloes_count}
        onSelect={(mode) => handlePdf(item, mode)}
      />
      {canManage && (
        <>
          <Button size="sm" className={appDialogSecondaryButton} onClick={() => openDuplicate(item)} title="Duplicar">
            <Copy className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            className="border border-red-200 bg-white text-red-600 hover:bg-red-50"
            onClick={() => setDeleteItem(item)}
            title="Excluir"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </>
      )}
    </div>
  );

  return (
    <Main>
      <PageHeader
        icon={ListChecks}
        title="Bolões escolhidos"
        description="Seleções de bolões montadas por concurso."
      >
        {canManage && (
          <Button className="bg-blue-600 text-white hover:bg-blue-700" onClick={() => navigate("/formador-de-boloes/novo")}>
            <Plus className="mr-2 h-4 w-4" />
            Nova seleção
          </Button>
        )}
      </PageHeader>

      <Card>
        <CardContent className="space-y-4 p-4 sm:p-6">
          <form
            className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_16rem_auto] sm:items-end"
            onSubmit={(e) => {
              e.preventDefault();
              applyFilters();
            }}
          >
            <div className="space-y-2">
              <Label>Pesquisar concurso</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Ex.: 3071"
                  className="pl-8"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Loteria</Label>
              <Select value={lotteryFilter} onValueChange={setLotteryFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {lotteries.map((lottery) => (
                    <SelectItem key={lottery.id} value={String(lottery.id)}>
                      {lottery.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button type="button" className={appDialogSecondaryButton} onClick={clearFilters}>
                Limpar
              </Button>
              <Button type="submit" className={appDialogPrimaryButton}>
                Filtrar
              </Button>
            </div>
          </form>

          {loading && !data ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : !data || data.data.length === 0 ? (
            <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
              Nenhuma seleção encontrada.
              {canManage && (
                <div className="mt-3">
                  <Button className="bg-blue-600 text-white hover:bg-blue-700" onClick={() => navigate("/formador-de-boloes/novo")}>
                    <Plus className="mr-2 h-4 w-4" />
                    Nova seleção
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border">
              <div className="hidden grid-cols-[11rem_6rem_1fr_24rem] gap-4 bg-muted/60 px-4 py-3 text-sm font-semibold lg:grid">
                <span>Loteria</span>
                <span>Concurso</span>
                <span>Bolões selecionados</span>
                <span>Ações</span>
              </div>
              {data.data.map((item) => (
                <div
                  key={item.uuid}
                  className="flex flex-col gap-3 border-t px-4 py-3 lg:grid lg:grid-cols-[11rem_6rem_1fr_24rem] lg:items-center lg:gap-4"
                >
                  <div className="flex items-center justify-between gap-2 lg:block">
                    {item.lottery && <LotteryBadge name={item.lottery.name} color={item.lottery.color} />}
                    <span className="text-sm font-medium lg:hidden">Concurso {item.contest?.name}</span>
                  </div>
                  <span className="hidden text-sm font-medium lg:block">{item.contest?.name}</span>
                  <span className="text-sm">
                    <strong>{item.boloes_count}</strong> {item.boloes_count === 1 ? "bolão" : "bolões"}
                    <span className="text-muted-foreground">
                      {" "}
                      · {item.games_count} {item.games_count === 1 ? "jogo" : "jogos"}
                    </span>
                  </span>
                  <div>{rowActions(item)}</div>
                </div>
              ))}
            </div>
          )}

          {data && data.total > 0 && (
            <div className="flex flex-col items-center justify-between gap-3 text-sm sm:flex-row">
              <span className="text-muted-foreground">
                Exibindo {data.from}–{data.to} de {data.total} {data.total === 1 ? "seleção" : "seleções"}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  className={appDialogSecondaryButton}
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((current) => current - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span>
                  Página {data.current_page} de {data.last_page}
                </span>
                <Button
                  size="sm"
                  className={appDialogSecondaryButton}
                  disabled={page >= data.last_page || loading}
                  onClick={() => setPage((current) => current + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Duplicar: copia os bolões (jogos, dezenas e cotas) para outro concurso, sem os jogos do banco */}
      <Dialog open={!!duplicateItem} onOpenChange={(open) => !open && !duplicating && setDuplicateItem(null)}>
        <AppDialogContent
          icon={Copy}
          title="Duplicar seleção"
          description={
            duplicateItem
              ? `${duplicateItem.lottery?.name} · concurso ${duplicateItem.contest?.name}`
              : undefined
          }
          className="sm:max-w-md"
        >
          <AppDialogBody>
            <div className="space-y-2">
              <Label>Concurso de destino</Label>
              <Select value={duplicateContestId} onValueChange={setDuplicateContestId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o concurso" />
                </SelectTrigger>
                <SelectContent>
                  {contests.map((contest) => (
                    <SelectItem key={contest.id} value={String(contest.id)}>
                      {contest.name}
                      {contest.id === duplicateItem?.contest?.id ? " (mesmo concurso)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {contests.length === 0 && (
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  Não há concurso ativo desta loteria para receber a cópia.
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                Os bolões (jogos, dezenas e cotas) são copiados sem os jogos do banco: cada jogo só pode estar em uma
                seleção, então você escolhe os jogos da cópia depois. Pode ser outro concurso ou o mesmo, para uma variação.
              </p>
            </div>
          </AppDialogBody>
          <AppDialogFooter className="flex-col-reverse gap-2 sm:flex-row">
            <Button className={appDialogSecondaryButton} onClick={() => setDuplicateItem(null)} disabled={duplicating}>
              Cancelar
            </Button>
            <Button
              className={appDialogPrimaryButton}
              onClick={confirmDuplicate}
              disabled={!duplicateContestId || duplicating}
            >
              {duplicating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Duplicar
            </Button>
          </AppDialogFooter>
        </AppDialogContent>
      </Dialog>

      {/* Excluir: os jogos da seleção voltam ao banco */}
      <Dialog open={!!deleteItem} onOpenChange={(open) => !open && !deleting && setDeleteItem(null)}>
        <AppDialogContent
          icon={Trash2}
          title="Excluir seleção"
          description={
            deleteItem ? `${deleteItem.lottery?.name} · concurso ${deleteItem.contest?.name}` : undefined
          }
          className="sm:max-w-md"
        >
          <AppDialogBody>
            <p className="text-sm">
              Excluir esta seleção apaga os <strong>{deleteItem?.boloes_count}</strong> bolões e libera os{" "}
              <strong>{deleteItem?.games_count}</strong> jogos, que voltam a aparecer no banco para novas escolhas. Não
              dá para desfazer.
            </p>
          </AppDialogBody>
          <AppDialogFooter className="flex-col-reverse gap-2 sm:flex-row">
            <Button className={appDialogSecondaryButton} onClick={() => setDeleteItem(null)} disabled={deleting}>
              Cancelar
            </Button>
            <Button className="bg-red-600 text-white hover:bg-red-700" onClick={confirmDelete} disabled={deleting}>
              {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Excluir seleção
            </Button>
          </AppDialogFooter>
        </AppDialogContent>
      </Dialog>
    </Main>
  );
};
